import { Doc } from "@smartsuite/smartdoc";

import { SentryService } from "../services/SentryService/SentryService";

export interface EditorMessage {
  type: string;
  doc?: Doc;
  yjsData?: string | null;

  [key: string]: string | number | null | Doc | undefined;
}

/**
 * Checks if we are rendering in a `react-native-webview` context.
 * We do that by checking if there's a `window.ReactNativeWebView.postMessage` function
 */
export function isReactNativeWebViewContext(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).ReactNativeWebView && (window as any).ReactNativeWebView.postMessage;
}

/**
 * Post a typed editor message using `react-native-webview` function `window.ReactNativeWebView.postMessage`.
 * The message must be a raw string, so we first convert our message object to a JSON string using `JSON.stringify`.
 * The message must be parsed back by the `react-native-webview` client.
 */
export function postMessage(message: EditorMessage): void {
  postRawMessage(JSON.stringify(message), message.type);
}

/**
 * The bridge can go away while the page is still alive, and there is no synchronous way
 * to detect it: on iOS `react-native-webview` injects a `postMessage` that forwards to
 * `window.webkit.messageHandlers`, which WKWebView tears down when the webview is
 * deallocated or the host screen unmounts. The injected function object survives on
 * `window`, so `isReactNativeWebViewContext()` still reports a webview and the call
 * throws from inside the injected code.
 *
 * The bridge never comes back, so the first failure is treated as terminal: reporting it
 * once keeps the signal, and skipping later calls avoids re-serialising whole documents
 * for a destination that cannot receive them.
 */
let isBridgeUnreachable = false;

/**
 * Post a raw string message using `react-native-webview` function `window.ReactNativeWebView.postMessage`
 */
function postRawMessage(message: string, messageType?: string): void {
  if (isBridgeUnreachable || !isReactNativeWebViewContext()) {
    return;
  }

  // The type only: it shows what the widget was telling the native app right before an
  // event, and the body is customer content.
  SentryService.addBreadcrumb({ category: "bridge", message: `post ${messageType}` });

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ReactNativeWebView.postMessage(message);
  } catch (error) {
    isBridgeUnreachable = true;

    SentryService.captureException({
      error: error instanceof Error ? error : new Error(String(error)),
      // Expected during teardown, so it must not take the widget down through the error
      // boundary - but it does mean editor messages stop reaching the native app.
      level: "warning",
      // NOTE: the message body is deliberately left out, it carries customer content.
      data: { messageType, reason: "The react-native-webview bridge is no longer reachable" },
    });
  }
}

export function getInjectedObjectJson(): string | null {
  if (!isReactNativeWebViewContext()) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = (window as any).ReactNativeWebView.injectedObjectJson?.();

  if (!result) {
    return null;
  }

  return result;
}
